/**
 * Administrationen (URL: /arbejdsrum): overblik, holdoversigt, holdadministration med fremmøde og
 * beskedformular.
 *
 * Menupunkter vises kun, hvor rollen giver adgang — men serveren håndhæver
 * altid rettighederne uafhængigt af, hvad brugerfladen viser (§4).
 */

import { useState } from 'react';
import {
  antal,
  api,
  departmentLabel,
  formatDateTime,
  isRole,
  ROLE_DESCRIPTIONS,
  roleLabel,
  scheduleText,
  todayIso,
} from '../lib/api.js';
import { Link, useLocation } from '../lib/router.js';
import {
  ActionButton,
  AsyncSection,
  Badge,
  departmentBadgeKind,
  EmptyState,
  Field,
  Meta,
  Notice,
  PageHead,
  SectionHead,
  useAction,
  useAsync,
} from '../components/ui.js';
import { Icon, departmentIcon, type IconName } from '../components/icons.js';
import {
  DEPARTMENTS,
  DEPARTMENT_LABELS,
  repliesEnabledByDefault,
  type Audience,
} from '../../shared/domain.js';

const WEEKDAYS = ['mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag', 'søndag'];

/* ------------------------------------------------------------------ */
/* Navigation                                                          */
/* ------------------------------------------------------------------ */

/**
 * Administrationens navigation.
 *
 * Punkterne vises kun, hvor rollen giver adgang — men serveren håndhæver
 * altid rettighederne uafhængigt af, hvad brugerfladen viser. Hvert punkt
 * har et pictogram, så en instruktør, der bruger administrationen én gang om
 * ugen, kan finde det rigtige sted uden at læse hele rækken.
 */
export function WorkspaceNav({ role }: { role: string }): JSX.Element {
  const { pathname } = useLocation();
  const boardish = role === 'board' || role === 'admin';
  const items: Array<{ to: string; label: string; icon: IconName }> = [
    { to: '/arbejdsrum', label: 'Overblik', icon: 'overview' },
    { to: '/arbejdsrum/hold', label: 'Hold', icon: 'teams' },
    { to: '/arbejdsrum/saeson', label: 'Sæson', icon: 'season' },
    { to: '/arbejdsrum/arrangementer', label: 'Arrangementer', icon: 'calendar' },
    // Instruktører: udvalg og opefter. En instruktør må ikke udpege sin egen
    // afløser — det er afdelingens beslutning, ikke holdets.
    ...(boardish || role === 'committee'
      ? [{ to: '/arbejdsrum/instruktoerer', label: 'Instruktører', icon: 'roles' as IconName }]
      : []),
    ...(boardish
      ? [
          { to: '/arbejdsrum/roller', label: 'Roller', icon: 'roles' as IconName },
          { to: '/arbejdsrum/statistik', label: 'Statistik', icon: 'stats' as IconName },
        ]
      : []),
    // Conventus er administratorens. Punktet skjules for alle andre —
    // en bekvemmelighed, ikke sikkerheden: serveren afviser uanset hvad.
    ...(role === 'admin'
      ? [{ to: '/arbejdsrum/conventus', label: 'Conventus', icon: 'conventus' as IconName }]
      : []),
  ];
  return (
    <nav className="workspace__nav" aria-label="Administration">
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          aria-current={pathname === item.to ? 'page' : undefined}
        >
          <Icon name={item.icon} size={18} active={pathname === item.to} />
          {item.label}
        </Link>
      ))}
    </nav>
  );
}

/**
 * «Hvor er jeg, og hvordan kommer jeg tilbage.» Står øverst på de sider i
 * administrationen, man kan komme ind på fra en liste — uden den kan man kun
 * bruge browserens tilbageknap, og på en telefon i en app-ramme findes den
 * ikke altid.
 */
export function WorkspaceCrumbs({
  to,
  label,
}: {
  to: string;
  label: string;
}): JSX.Element {
  return (
    <nav className="crumbs" aria-label="Sti">
      <Link to={to}>
        <Icon name="back" size={16} />
        {label}
      </Link>
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/* Overblik                                                            */
/* ------------------------------------------------------------------ */

interface WorkspaceData {
  scope: { role: string; departments: string[]; teamCount: number; isBoard: boolean };
  teams: Array<{ teamId: string; slug: string; name: string; department: string }>;
  announcements: Array<{
    id: string;
    title: string;
    audience: string;
    priority: string;
    publishedAt: string;
    createdByName: string;
    pushResult: { accepted: number; attempted: number; note: string } | null;
    /* Afgjort på serveren. Se kommentaren ved payloaden i routes/workspace.ts. */
    canRemove: boolean;
  }>;
  absences: Array<{
    id: string;
    personName: string;
    teamName: string;
    occurrenceDate: string;
    reasonLabel: string;
  }>;
  absenceNote: string;
  volunteers: Array<{ id: string; title: string; startsAt: string; signedUp: number; capacity: number }>;
  teamsAwaitingTrainer: number;
  teamsNeedingReview: Array<{ teamId: string; name: string; dataReview: string }>;
}

/**
 * Beskrivelsen af, hvad rollen giver adgang til.
 *
 * Udvalgsmedlemmets beskrivelse er den eneste, der afhænger af data: den
 * nævner de afdelinger, brugeren faktisk har fået. Resten står i
 * domænelaget, hvor rollerne bor.
 */
export function accessDescription(scope: { role: string; departments: string[] }): string {
  if (scope.role === 'committee') {
    return scope.departments.length > 0
      ? `Adgang til alle hold i ${scope.departments.map(departmentLabel).join(', ')}.`
      : 'Ingen afdeling tildelt endnu — der er derfor ingen hold at se.';
  }
  // `isRole` og ikke et opslag med en løs streng: kommer der en rolle,
  // appen ikke kender, skal den sige det og ikke låne en anden rolles tekst.
  return isRole(scope.role)
    ? ROLE_DESCRIPTIONS[scope.role]
    : 'Rollen er ukendt for denne udgave af appen.';
}


/* ------------------------------------------------------------------ */
/* Pushnøgler                                                          */
/* ------------------------------------------------------------------ */

/**
 * Genererer et VAPID-nøglepar til pushnotifikationer — I BROWSEREN.
 *
 * Hele pushkæden er bygget: service worker, tilmelding under Profil,
 * afsender på serveren. Der mangler kun to miljøvariable, og de kan ikke
 * bare udleveres af nogen: den private nøgle er det, der beviser over for
 * Google og Apple, at beskeden kommer fra foreningen.
 *
 * DERFOR ER DEN HER OG IKKE I EN CHAT ELLER PÅ EN FREMMED HJEMMESIDE.
 * `crypto.subtle` kører i browseren, nøglen bliver til på administratorens
 * egen maskine, og den sendes ingen steder hen — heller ikke til vores egen
 * server. Den skrives ét sted: ind i Railways secret-lager, i hånden.
 * Præcis den fejl blev begået én gang med krypteringsnøglen, som blev lavet
 * i en samtale og siden har skullet skiftes.
 *
 * Formatet skal ramme `importVapidSigningKey` på serveren nøjagtigt:
 * den offentlige nøgle er de rå 65 bytes (0x04 ‖ x ‖ y) i base64url, og den
 * private er JWK-feltet `d`. Rammer det ved siden af, fejler serveren først
 * den dag, nogen prøver at sende en besked.
 */
export function base64url(bytes: Uint8Array): string {
  let binært = '';
  for (const b of bytes) binært += String.fromCharCode(b);
  return btoa(binært).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface DemoCleanupReport {
  accounts: Array<{ email: string; name: string }>;
  announcements: number;
  volunteerShifts: number;
  attendanceRecords: number;
  untouched: { seasons: number; sessions: number; teams: number; closures: number };
}

/**
 * Klargøring før rigtige medlemmer lukkes ind.
 *
 * Kortet vises kun for administratoren, og kun så længe der FINDES
 * demodata. Når der er ryddet op, forsvinder det af sig selv — en knap, der
 * bliver stående for evigt, bliver til støj og til sidst til noget, nogen
 * trykker på ved et uheld.
 *
 * Der er to trin med vilje. Først vises hvad der ville blive fjernet, med
 * navn og adresse på hver eneste konto. Så kan man trykke. En sletteknap,
 * der ikke først viser hvad den sletter, tør ingen trykke på — og noget,
 * ingen tør trykke på, bliver aldrig gjort.
 */
export function DemoCleanupCard({ onDone }: { onDone: () => void }): JSX.Element | null {
  const report = useAsync<DemoCleanupReport>(() => api.get('/api/workspace/demo-oprydning'));
  const action = useAction();
  const [confirming, setConfirming] = useState(false);

  const data = report.data;
  if (!data || data.accounts.length === 0) return null;

  const ryd = (): void => {
    void action.run(async () => {
      const res = await api.post<{ removedAccounts: number }>('/api/workspace/demo-oprydning');
      action.setSuccess(
        `${antal(res.removedAccounts, 'testkonto er fjernet', 'testkonti er fjernet')}. ` +
          'Sæson, hold og kalender står urørt.',
      );
      setConfirming(false);
      report.reload();
      onDone();
    });
  };

  return (
    <section className="section" aria-labelledby="klargoering">
      <SectionHead title="Klargøring før medlemmer" icon="warning" id="klargoering" />
      <div className="card">
        <p className="small">
          Databasen indeholder stadig demodata. Lukkes appen op for foreningens
          familier nu, møder de opdigtede konti og opdigtede beskeder.
        </p>

        <div className="meta mt-3">
          <Meta icon="profile" label="Testkonti, der fjernes">
            {data.accounts.map((a) => a.email).join(', ')}
          </Meta>
          <Meta icon="message" label="Opdigtede beskeder">
            {antal(data.announcements, 'besked', 'beskeder')}
          </Meta>
          <Meta icon="volunteer" label="Opdigtede frivilligopgaver">
            {antal(data.volunteerShifts, 'opgave', 'opgaver')}
          </Meta>
          <Meta icon="attendance" label="Opdigtet fremmøde">
            {antal(data.attendanceRecords, 'registrering', 'registreringer')}
          </Meta>
        </div>

        {/*
          Det, der IKKE røres, står lige så tydeligt som det, der fjernes.
          Det er dét, der gør knappen mulig at trykke på: demoen kørte på en
          tom database ved allerførste opstart, så det er demoens
          bestyrelseskonto, der står som opretter af sæsonen. Uden den her
          linje ville enhver med rette frygte at miste kalenderen.
        */}
        <div className="notice notice--info mt-3">
          <p className="small">
            <strong>Dette røres ikke:</strong> {antal(data.untouched.seasons, 'sæson', 'sæsoner')},{' '}
            {antal(data.untouched.sessions, 'træning', 'træninger')},{' '}
            {antal(data.untouched.teams, 'hold', 'hold')} og{' '}
            {antal(data.untouched.closures, 'ferieperiode', 'ferieperioder')} — heller ikke selv om
            demokontoen står som den, der oprettede dem.
          </p>
        </div>

        {action.error ? (
          <div className="mt-3">
            <Notice kind="error">{action.error}</Notice>
          </div>
        ) : null}
        {action.success ? (
          <div className="mt-3">
            <Notice kind="success">{action.success}</Notice>
          </div>
        ) : null}

        <div className="mt-3">
          {confirming ? (
            <div className="btn-group">
              <ActionButton className="btn btn--danger" pending={action.pending} onClick={ryd}>
                Ja, fjern demodata
              </ActionButton>
              <button type="button" className="btn btn--secondary" onClick={() => setConfirming(false)}>
                Fortryd
              </button>
            </div>
          ) : (
            <button type="button" className="btn btn--secondary" onClick={() => setConfirming(true)}>
              <Icon name="warning" size={17} />
              Fjern demodata
            </button>
          )}
        </div>

        <p className="tiny muted mt-3">
          Slå også <code>DEMO_MODE</code> fra i Railway. Så forsvinder båndet
          øverst, og demoen kan ikke oprettes igen.
        </p>
      </div>
    </section>
  );
}


/* ------------------------------------------------------------------ */
/* En sendt besked — og muligheden for at fortryde den                  */
/* ------------------------------------------------------------------ */

/**
 * EN BESKED SKAL KUNNE FJERNES IGEN.
 *
 * Grunden er hverdagsagtig: den første, der prøver «Send besked» af,
 * skriver «test». Uden den her knap står det «test» i indbakken hos alle,
 * der får beskeder — også når der er 400 af dem. Det er nok til, at ingen
 * tør trykke på Send første gang.
 *
 * To trin, som ved oprydningen efter demoen: serveren siger først hvad der
 * ryger med, og først derefter kan der trykkes. En sletteknap, der ikke
 * viser hvad den sletter, tør ingen bruge.
 *
 * OG DET, DER IKKE KAN FORTRYDES, STÅR DER OGSÅ. En push er ude af huset i
 * samme øjeblik den sendes. Beskeden forsvinder fra appen; notifikationen
 * på nogens låseskærm gør den ikke.
 */
function SentMessageRow({
  besked,
  onRemoved,
}: {
  besked: WorkspaceData['announcements'][number];
  onRemoved: () => void;
}): JSX.Element {
  const [forhåndsvisning, setForhåndsvisning] = useState<{ replies: number } | null>(null);
  const action = useAction();

  const spørg = (): void => {
    void action.run(async () => {
      const res = await api.get<{ title: string; replies: number }>(
        `/api/announcements/${encodeURIComponent(besked.id)}/fjern`,
      );
      setForhåndsvisning({ replies: res.replies });
    });
  };

  const fjern = (): void => {
    void action.run(async () => {
      await api.del(`/api/announcements/${encodeURIComponent(besked.id)}`);
      setForhåndsvisning(null);
      onRemoved();
    });
  };

  return (
    <li>
      <div className="row row--between">
        <span className="small">
          <strong>{besked.title}</strong>
        </span>
        {besked.priority === 'important' ? <Badge kind="important">Vigtig</Badge> : null}
      </div>
      <p className="tiny muted">
        {formatDateTime(besked.publishedAt)} · {besked.createdByName}
        {besked.pushResult
          ? ` · push: ${besked.pushResult.accepted}/${besked.pushResult.attempted} accepteret`
          : ''}
      </p>

      {action.error ? (
        <div className="mt-2">
          <Notice kind="error">{action.error}</Notice>
        </div>
      ) : null}

      {besked.canRemove ? (
        forhåndsvisning ? (
          <div className="notice notice--warning mt-2">
            <p className="small">
              <strong>Fjern «{besked.title}»?</strong>
            </p>
            <p className="small mt-1">
              Beskeden forsvinder fra indbakken hos alle, der har fået den.
              {forhåndsvisning.replies > 0
                ? ` ${antal(forhåndsvisning.replies, 'svar til dig', 'svar til dig')} følger med og kan ikke hentes tilbage.`
                : ''}
              {besked.pushResult
                ? ' Pushbeskeden er allerede sendt og kan ikke kaldes tilbage — den kan stadig stå på nogens telefon.'
                : ''}
            </p>
            <div className="btn-group mt-3">
              <ActionButton className="btn btn--danger btn--small" pending={action.pending} onClick={fjern}>
                Ja, fjern beskeden
              </ActionButton>
              <button
                type="button"
                className="btn btn--secondary btn--small"
                onClick={() => setForhåndsvisning(null)}
              >
                Fortryd
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-2">
            <ActionButton className="btn btn--ghost btn--small" pending={action.pending} onClick={spørg}>
              Fjern beskeden
            </ActionButton>
          </p>
        )
      ) : null}
    </li>
  );
}

export function PushKeysCard(): JSX.Element {
  const [nøgler, setNøgler] = useState<{ offentlig: string; privat: string } | null>(null);
  const [fejl, setFejl] = useState<string | null>(null);
  const [travl, setTravl] = useState(false);
  const [kopieret, setKopieret] = useState<string | null>(null);

  async function kopiér(navn: string, værdi: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(værdi);
      setKopieret(navn);
      setTimeout(() => setKopieret((n) => (n === navn ? null : n)), 2000);
    } catch {
      // Udklipsholderen kan være spærret. Værdien står på skærmen og kan
      // markeres i hånden — så det er en skuffelse, ikke en blindgyde.
      setFejl('Kunne ikke kopiere automatisk. Markér værdien og kopiér den i hånden.');
    }
  }

  async function generér(): Promise<void> {
    setFejl(null);
    setTravl(true);
    try {
      const par = await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, true, [
        'sign',
        'verify',
      ]);
      const jwk = await crypto.subtle.exportKey('jwk', par.privateKey);
      const rå = await crypto.subtle.exportKey('raw', par.publicKey);
      const offentlig = base64url(new Uint8Array(rå));
      if (!jwk.d || new Uint8Array(rå).byteLength !== 65) {
        throw new Error('Browseren gav et nøglepar i et uventet format.');
      }
      setNøgler({ offentlig, privat: jwk.d });
    } catch (e) {
      setFejl(
        e instanceof Error
          ? `Nøglerne kunne ikke laves: ${e.message}`
          : 'Nøglerne kunne ikke laves.',
      );
    } finally {
      setTravl(false);
    }
  }

  return (
    <section className="card stack stack--tight">
      <h2>Pushnotifikationer er ikke slået til</h2>
      <p className="small muted">
        Alt er bygget undtagen to nøgler. Uden dem ligger en aflysning kun i
        indbakken — der kommer ingen besked frem på nogens telefon.
      </p>
      <p className="tiny muted mt-2">
        Nøglerne laves <strong>her i din browser</strong> og sendes ingen steder hen — heller
        ikke til appens egen server. Skriv dem ind i Railway med det samme; siden husker
        dem ikke, og den private nøgle kan ikke hentes frem igen.
      </p>

      {fejl ? <Notice kind="error">{fejl}</Notice> : null}

      {nøgler ? (
        <>
          <p className="small mt-3">
            Railway → <strong>skals-gf</strong> → Variables. Opret de tre variable herunder,
            og udrul derefter.
          </p>
          {/*
            Én kopiknap pr. værdi frem for én blok, man selv skal markere.
            Nøglerne er 43 og 87 tegn uden mellemrum; et enkelt tegn for lidt
            giver ikke en fejlmeddelelse nogen steder — serveren starter fint,
            og først den dag nogen sender en aflysning, sker der ingenting.
          */}
          {[
            { navn: 'VAPID_PUBLIC_KEY', værdi: nøgler.offentlig },
            { navn: 'VAPID_PRIVATE_KEY', værdi: nøgler.privat },
            { navn: 'VAPID_SUBJECT', værdi: 'mailto:kontakt@skalsgf.dk' },
          ].map((r) => (
            <div key={r.navn} className="card mt-2">
              <p className="tiny muted">{r.navn}</p>
              <p className="mono tiny" style={{ wordBreak: 'break-all', margin: '4px 0 8px' }}>
                {r.værdi}
              </p>
              <button
                type="button"
                className="btn btn--secondary btn--small"
                onClick={() => void kopiér(r.navn, r.værdi)}
              >
                {kopieret === r.navn ? 'Kopieret' : 'Kopiér'}
              </button>
            </div>
          ))}
          <Notice kind="warning">
            Den private nøgle må ikke sendes i en mail eller en chat og må ikke gemmes i
            GitHub. Kommer den ud, kan andre sende pushbeskeder i foreningens navn.
          </Notice>
          <p className="tiny muted">
            Når de tre variable er sat, og appen er udrullet, forsvinder dette felt af sig
            selv. Derefter kan du slå push til under Profil.
          </p>
          {/*
            Det, der ellers ville blive opdaget af 400 forældre på én gang.
          */}
          <Notice kind="info">
            <strong>På iPhone virker push kun fra hjemmeskærmen.</strong> Apple sender ikke
            pushbeskeder til en side, der er åbnet i Safari — appen skal først lægges på
            hjemmeskærmen med «Del → Føj til hjemmeskærm». På Android virker det uden.
            Det skal stå i introduktionen til medlemmerne, ellers tror halvdelen, at
            notifikationerne er i stykker.
          </Notice>
        </>
      ) : (
        <p className="mt-3">
          <button type="button" className="btn" disabled={travl} onClick={() => void generér()}>
            {travl ? 'Laver nøgler …' : 'Lav nøgler til push'}
          </button>
        </p>
      )}
    </section>
  );
}

export function WorkspacePage(): JSX.Element {
  const state = useAsync<WorkspaceData>(() => api.get('/api/workspace'));
  // Samme endepunkt, som profilen bruger til at afgøre, om der kan slås push
  // til. Er den `false`, mangler nøglerne — og så skal administratoren kunne
  // gøre noget ved det uden at skulle vide, hvad VAPID er.
  const pushStatus = useAsync<{ enabled: boolean }>(() => api.get('/api/push/config'));

  return (
    <div className="stack">
      <PageHead title="Administration" />
      <AsyncSection state={state} skeletonLines={6}>
        {(data) => (
          <>
            {/*
              NØGLETAL ØVERST.
              Administrationen er et arbejdsredskab, og den, der åbner den,
              vil vide, om der er noget at tage sig af — ikke læse en
              forside. Tre tal, der kan aflæses uden at rulle.

              Tallene er valgt, fordi de kan KRÆVE noget. «Antal medlemmer»
              ville være et pænt tal, men der er ikke noget at gøre ved det.
              Afbud og manglende instruktører er opgaver.

              Et tal, der kræver handling, står rødt — men ordet under
              forklarer det, så farven aldrig står alene.
            */}
            <section className="section" aria-label="Nøgletal">
              <div className="stats">
                <div className="stats__cell">
                  <span className="stats__value">{data.scope.teamCount}</span>
                  {/* Etiketten gentager IKKE tallet ovenover. «62 / 62 hold
                      inden for din adgang» er det samme tal to gange. */}
                  <span className="stats__label">hold inden for din adgang</span>
                </div>
                <div className={`stats__cell${data.absences.length > 0 ? ' stats__cell--flag' : ''}`}>
                  <span className="stats__value">{data.absences.length}</span>
                  <span className="stats__label">
                    afbud til {data.absences.length === 1 ? 'en kommende træning' : 'kommende træninger'}
                  </span>
                </div>
                <div
                  className={`stats__cell${data.teamsAwaitingTrainer > 0 ? ' stats__cell--flag' : ''}`}
                >
                  <span className="stats__value">{data.teamsAwaitingTrainer}</span>
                  <span className="stats__label">
                    hold mangler instruktør
                  </span>
                </div>
              </div>
            </section>

            {/*
              Adgangen står øverst og siger med rene ord, hvad brugeren må
              her. En instruktør, der ser færre hold end forventet, skal
              kunne se HVORFOR uden at spørge nogen.

              Navn og beskrivelse slås op i domænelaget. Her stod før en
              ternærkæde, der kun kendte `board` og `committee`; alt andet
              faldt igennem til «Instruktør — kun de hold, du er tildelt».
              En administrator blev derfor kaldt instruktør, mens han sad og
              så samtlige hold. Et opslag kan ikke gætte på samme måde.
            */}
            <section className="section" aria-labelledby="din-adgang">
              <SectionHead title="Din adgang" icon="roles" id="din-adgang" />
              <div className="card">
                <div className="meta">
                  <Meta icon="roles" label="Rolle">
                    {roleLabel(data.scope.role)} — {accessDescription(data.scope)}
                  </Meta>
                  <Meta icon="teams" label="Omfang">
                    {data.scope.teamCount} hold inden for din adgang.
                  </Meta>
                </div>
              </div>
            </section>

            {/*
              Kortet står kun her, mens push mangler, og kun for
              administratoren. Er nøglerne sat, forsvinder det af sig selv —
              en opsætningsknap, der bliver stående for evigt, bliver til
              støj og til sidst til noget, nogen trykker på ved et uheld.
            */}
            {data.scope.role === 'admin' ? <DemoCleanupCard onDone={state.reload} /> : null}

            {data.scope.role === 'admin' ? <BackupCard /> : null}

            {data.scope.role === 'admin' && pushStatus.data && !pushStatus.data.enabled ? (
              <PushKeysCard />
            ) : null}

            {data.teamsNeedingReview.length > 0 ? (
              <Notice kind="warning">
                <strong>Hold med uafklaret træningsdato</strong>
                <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                  {data.teamsNeedingReview.map((t) => (
                    <li key={t.teamId} className="small">
                      {t.name} — {t.dataReview}
                    </li>
                  ))}
                </ul>
              </Notice>
            ) : null}

            <section className="section" id="beskeder" aria-labelledby="ws-beskeder">
              <SectionHead
                title="Beskeder"
                icon="message"
                id="ws-beskeder"
                action={
                  <Link className="btn btn--small" to="/arbejdsrum/hold">
                    <Icon name="plus" size={17} />
                    Send besked
                  </Link>
                }
              />
              <div className="card">
              {data.announcements.length === 0 ? (
                <EmptyState title="Ingen sendte beskeder" icon="message">
                  Beskeder, du eller din afdeling har sendt, vises her.
                </EmptyState>
              ) : (
                <ul className="rows">
                  {data.announcements.slice(0, 8).map((a) => (
                    <SentMessageRow key={a.id} besked={a} onRemoved={state.reload} />
                  ))}
                </ul>
              )}
              </div>
            </section>

            <section className="section" id="afbud" aria-labelledby="ws-afbud">
              <SectionHead title="Afbud" icon="absence" id="ws-afbud" />
              <div className="card">
              <p className="tiny muted">{data.absenceNote}</p>
              {data.absences.length === 0 ? (
                <EmptyState title="Ingen kommende afbud" icon="absence">
                  Der er ikke meldt afbud til de hold, du har adgang til.
                </EmptyState>
              ) : (
                <ul className="rows mt-3">
                  {data.absences.map((a) => (
                    <li key={a.id} className="row row--between">
                      <span className="small">
                        <strong>{a.personName}</strong>
                        <span className="muted"> · {a.teamName}</span>
                      </span>
                      <span className="tiny muted">
                        {a.occurrenceDate} · {a.reasonLabel}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              </div>
            </section>

            <section className="section" id="frivillige" aria-labelledby="ws-frivillige">
              <SectionHead title="Frivillige opgaver" icon="volunteer" id="ws-frivillige" />
              <div className="card">
              {data.volunteers.length === 0 ? (
                <EmptyState title="Ingen opgaver" icon="volunteer">
                  Der er ikke oprettet kommende frivillige opgaver.
                </EmptyState>
              ) : (
                <ul className="rows">
                  {data.volunteers.map((v) => (
                    <li key={v.id} className="row row--between">
                      <span className="small">{v.title}</span>
                      <span className="tiny muted">
                        {v.signedUp}/{v.capacity} · {formatDateTime(v.startsAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-3">
                <NewShiftForm onCreated={state.reload} />
              </p>
              </div>
            </section>

            <section className="section" id="hold" aria-labelledby="ws-hold">
              <SectionHead
                title="Hold"
                icon="teams"
                id="ws-hold"
                action={
                  <Link className="section__link" to="/arbejdsrum/hold">
                    Se alle
                  </Link>
                }
              />
              <div className="card">
                <p className="small muted">
                  {data.scope.teamCount} hold inden for din adgang.
                </p>
                {data.teamsAwaitingTrainer > 0 ? (
                  <p className="mt-2">
                    <Badge kind="warning" icon="trainer">
                      {data.teamsAwaitingTrainer} hold afventer instruktør
                    </Badge>
                  </p>
                ) : null}
              </div>
            </section>
          </>
        )}
      </AsyncSection>
    </div>
  );
}

function NewShiftForm({ onCreated }: { onCreated: () => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [capacity, setCapacity] = useState('4');
  const action = useAction();

  if (!open) {
    return (
      <button type="button" className="btn btn--secondary btn--small" onClick={() => setOpen(true)}>
        <Icon name="plus" size={17} />
        Opret opgave
      </button>
    );
  }

  const submit = (): void => {
    void action.run(async () => {
      await api.post('/api/volunteer-shifts', {
        title,
        location,
        startsAt: new Date(startsAt).toISOString(),
        capacity: Number(capacity),
      });
      action.setSuccess('Opgaven er oprettet.');
      setTitle('');
      setLocation('');
      setStartsAt('');
      setOpen(false);
      onCreated();
    });
  };

  return (
    <div className="stack stack--tight">
      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      <Field label="Titel" htmlFor="shift-title" error={action.fieldError('title')}>
        <input
          id="shift-title"
          type="text"
          value={title}
          maxLength={100}
          onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
        />
      </Field>
      <Field label="Sted" htmlFor="shift-location" error={action.fieldError('location')}>
        <input
          id="shift-location"
          type="text"
          value={location}
          maxLength={100}
          onChange={(e) => setLocation((e.target as HTMLInputElement).value)}
        />
      </Field>
      <Field label="Starttidspunkt" htmlFor="shift-start" error={action.fieldError('startsAt')}>
        <input
          id="shift-start"
          type="datetime-local"
          value={startsAt}
          onChange={(e) => setStartsAt((e.target as HTMLInputElement).value)}
        />
      </Field>
      <Field
        label="Antal pladser"
        htmlFor="shift-capacity"
        hint="Mellem 1 og 100"
        error={action.fieldError('capacity')}
      >
        <input
          id="shift-capacity"
          type="number"
          min={1}
          max={100}
          value={capacity}
          onChange={(e) => setCapacity((e.target as HTMLInputElement).value)}
        />
      </Field>
      <div className="btn-group">
        <ActionButton pending={action.pending} onClick={submit}>
          Opret
        </ActionButton>
        <button type="button" className="btn btn--secondary" onClick={() => setOpen(false)}>
          Fortryd
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Holdoversigt                                                        */
/* ------------------------------------------------------------------ */

interface TeamListItem {
  teamId: string;
  slug: string;
  name: string;
  department: string;
  ageGroup: string;
  /** `membership` har ingen træningstider — felterne nedenfor er `null`. */
  kind: 'training' | 'membership';
  weekday: string | null;
  startTime: string | null;
  endTime: string | null;
  location: string;
  awaitingTrainer: boolean;
  trainerStatus: 'has_trainer' | 'awaiting' | 'unknown';
  dataReview: string | null;
  counts: { participants: number; trainers: number; helpers: number; unresolved: number };
  sync: { status: string; lastSuccessAt: string | null; message: string };
  assignedAppTrainers: number;
}

export function WorkspaceTeamsPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('alle');
  const [weekday, setWeekday] = useState('alle');
  const [missingTrainer, setMissingTrainer] = useState(false);

  const query = new URLSearchParams({
    q: search,
    department,
    weekday,
    missingTrainer: String(missingTrainer),
  });
  const state = useAsync<{ teams: TeamListItem[]; total: number; dataSource: string; note: string }>(
    () => api.get(`/api/workspace/teams?${query.toString()}`),
    [search, department, weekday, missingTrainer],
  );

  return (
    <div className="stack">
      <PageHead title="Hold" sub="Hold inden for din adgang" />

      <section className="card stack stack--tight" aria-label="Filtre">
        <Field label="Søg" htmlFor="team-search" hint="Holdnavn, aldersgruppe eller sted">
          <input
            id="team-search"
            type="search"
            value={search}
            onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
            placeholder="fx Lillespring, 3-5 år, Hal 2"
          />
        </Field>
        <div className="row">
          <Field label="Afdeling" htmlFor="team-department">
            <select
              id="team-department"
              value={department}
              onChange={(e) => setDepartment((e.target as HTMLSelectElement).value)}
            >
              <option value="alle">Alle afdelinger</option>
              {DEPARTMENTS.map((d) => (
                <option key={d} value={d}>
                  {DEPARTMENT_LABELS[d]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Ugedag" htmlFor="team-weekday">
            <select
              id="team-weekday"
              value={weekday}
              onChange={(e) => setWeekday((e.target as HTMLSelectElement).value)}
            >
              <option value="alle">Alle ugedage</option>
              {WEEKDAYS.map((d) => (
                <option key={d} value={d}>
                  {d[0].toUpperCase() + d.slice(1)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={missingTrainer}
            onChange={(e) => setMissingTrainer((e.target as HTMLInputElement).checked)}
          />
          <span className="small">Kun hold uden registreret instruktør</span>
        </label>
      </section>

      <AsyncSection state={state}>
        {(data) => (
          <>
            <p className="small muted">
              {data.total} hold · Datakilde: {data.dataSource}
            </p>
            {data.teams.length === 0 ? (
              <div className="card">
                <EmptyState title="Ingen hold matcher" icon="search">
                  Prøv en anden søgning eller nulstil filtrene.
                </EmptyState>
              </div>
            ) : (
              data.teams.map((team) => (
                <Link
                  key={team.teamId}
                  className={`card card--link dept-stripe dept-stripe--${team.department}`}
                  to={`/arbejdsrum/hold/${team.slug}`}
                >
                  <div className="card__header">
                    <div>
                      <Badge
                        kind={departmentBadgeKind(team.department)}
                        icon={departmentIcon(team.department)}
                      >
                        {departmentLabel(team.department)}
                      </Badge>
                      <h3 className="mt-2">{team.name}</h3>
                    </div>
                    <Icon name="forward" size={18} className="muted" />
                  </div>
                  <div className="meta mt-3">
                    {scheduleText(team) ? (
                      <Meta icon="clock" label="Tid">
                        {scheduleText(team)}
                      </Meta>
                    ) : (
                      <Meta icon="season" label="Art">
                        Medlemskab · ingen faste træningstider
                      </Meta>
                    )}
                    {team.location ? (
                      <Meta icon="place" label="Sted">
                        {team.location}
                      </Meta>
                    ) : null}
                    <Meta icon="teams" label="Antal">
                      {antal(team.counts.participants, 'deltager', 'deltagere')} ·{' '}
                      {antal(team.counts.trainers, 'instruktør', 'instruktører')}
                      {team.assignedAppTrainers > 0
                        ? ` · ${team.assignedAppTrainers} med appadgang`
                        : ''}
                    </Meta>
                  </div>
                  <div className="row mt-3" style={{ gap: 6 }}>
                    {/*
                      Ét mærkat om instruktører, ikke to. Før stod der både
                      «Ingen registreret instruktør» (udledt af tallet) og
                      «Afventer instruktør» (et fast flag fra kataloget), og
                      de kunne modsige hinanden. Nu kommer svaret ét sted fra.
                    */}
                    {team.sync.status === 'failed' ? (
                      <Badge kind="warning" icon="error">
                        Hentefejl fra Conventus
                      </Badge>
                    ) : team.trainerStatus === 'unknown' ? (
                      <Badge icon="conventus">Ikke hentet fra Conventus endnu</Badge>
                    ) : team.trainerStatus === 'awaiting' ? (
                      <Badge kind="warning" icon="trainer">
                        Ingen instruktør i Conventus
                      </Badge>
                    ) : null}
                    {team.counts.unresolved > 0 ? (
                      <Badge kind="warning" icon="warning">
                        {team.counts.unresolved} uafklaret rolle
                      </Badge>
                    ) : null}
                    {team.dataReview ? (
                      <Badge kind="warning" icon="calendar">
                        Dato skal afklares
                      </Badge>
                    ) : null}
                  </div>
                  <p className="tiny muted mt-3">
                    {team.sync.lastSuccessAt
                      ? `Sidst synkroniseret ${formatDateTime(team.sync.lastSuccessAt)}`
                      : 'Endnu ikke synkroniseret'}
                  </p>
                </Link>
              ))
            )}
            <p className="tiny muted">{data.note}</p>
          </>
        )}
      </AsyncSection>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Holdadministration med fremmøde                                     */
/* ------------------------------------------------------------------ */

interface AttendanceEntry {
  memberId: string;
  name: string;
  status: 'present' | 'absent' | null;
  note: string;
  recordedByName: string;
}

interface TeamSession {
  id: string;
  sessionDate: string;
  status: 'scheduled' | 'cancelled' | 'moved' | 'closed';
  reason: string;
  isExtra: boolean;
  changedByName: string;
  effectiveDate: string;
  effectiveStartTime: string;
  effectiveEndTime: string;
  effectiveLocation: string;
}

interface AttendanceSheet {
  sessionId: string;
  teamId: string;
  teamName: string;
  date: string;
  status: TeamSession['status'];
  reason: string;
  entries: AttendanceEntry[];
  counts: { present: number; absent: number; unregistered: number };
  absences: Array<{ personKey: string; personName: string; reasonLabel: string; note: string }>;
  blockedReason: string | null;
}

interface TeamDetailData {
  team: TeamListItem;
  sessions: TeamSession[];
  selectedSessionId: string | null;
  attendance: AttendanceSheet | null;
  absences: Array<{ id: string; personName: string; date: string; reasonLabel: string }>;
  noSessionsReason: string | null;
}

const SESSION_STATUS_LABEL: Record<TeamSession['status'], string> = {
  scheduled: '',
  cancelled: 'aflyst',
  moved: 'flyttet',
  closed: 'ferielukket',
};

/** «tor. 16. okt · 17.00» — kort nok til en rulleliste på en telefon. */
function sessionLabel(session: TeamSession): string {
  const date = new Date(`${session.effectiveDate}T12:00:00Z`);
  const text = new Intl.DateTimeFormat('da-DK', {
    timeZone: 'Europe/Copenhagen',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date);
  const status = SESSION_STATUS_LABEL[session.status];
  return `${text} · ${session.effectiveStartTime}${status ? ` — ${status}` : ''}${
    session.isExtra ? ' — ekstra' : ''
  }`;
}

export function WorkspaceTeamDetailPage({ slug }: { slug: string }): JSX.Element {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const state = useAsync<TeamDetailData>(
    () =>
      api.get(
        `/api/workspace/teams/${encodeURIComponent(slug)}${
          sessionId ? `?session=${encodeURIComponent(sessionId)}` : ''
        }`,
      ),
    [slug, sessionId],
  );

  return (
    <div className="stack">
      <AsyncSection state={state} skeletonLines={6}>
        {(data) => (
          <>
            <WorkspaceCrumbs to="/arbejdsrum/hold" label="Alle hold" />

            <PageHead
              title={data.team.name}
              eyebrow={departmentLabel(data.team.department)}
              eyebrowIcon={departmentIcon(data.team.department)}
              sub={
                [scheduleText(data.team) ?? 'Medlemskab · ingen faste træningstider',
                 data.team.location].filter(Boolean).join(' · ')
              }
            />

            {/*
              Et medlemskab har ingen træninger, og det er ikke en fejl.
              «Der er ikke oprettet træninger» ville sende et
              udvalgsmedlem på jagt efter noget at rette — og «Ekstra
              træning» ville lade ham lægge en træning ind, som familierne
              ville få i kalenderen for et medlemskab uden mødetid.
            */}
            {data.team.kind === 'membership' ? (
              <Notice kind="info">
                Det her er et medlemskab, ikke et hold med faste træningstider. Derfor er der
                ingen kalender, intet fremmøde og ingen afbud — men beskeder virker som på
                ethvert andet hold.
              </Notice>
            ) : data.noSessionsReason ? (
              <Notice kind="warning">{data.noSessionsReason}</Notice>
            ) : (
              <>
                <section className="card">
                  <h2>Træningsgang</h2>
                  <div className="mt-3">
                    <Field
                      label="Vælg træning"
                      htmlFor="session-picker"
                      hint="Listen er sæsonens træninger — ikke en fri dato. Så kan der ikke registreres på en dag, holdet ikke træner."
                    >
                      <select
                        id="session-picker"
                        value={data.selectedSessionId ?? ''}
                        onChange={(e) => setSessionId((e.target as HTMLSelectElement).value)}
                      >
                        {data.sessions.map((session) => (
                          <option key={session.id} value={session.id}>
                            {sessionLabel(session)}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  {data.selectedSessionId ? (
                    <SessionControls
                      session={
                        data.sessions.find((s) => s.id === data.selectedSessionId) ?? data.sessions[0]
                      }
                      onChanged={state.reload}
                    />
                  ) : null}
                </section>

                <section className="card">
                  <h2>Fremmøde</h2>
                  {data.attendance?.blockedReason ? (
                    <div className="mt-3">
                      <Notice kind="warning">{data.attendance.blockedReason}</Notice>
                    </div>
                  ) : data.attendance ? (
                    <AttendanceEditor
                      sessionId={data.attendance.sessionId}
                      sheet={data.attendance}
                      onSaved={state.reload}
                    />
                  ) : null}
                </section>
              </>
            )}

            {data.team.kind === 'membership' ? null : (
            <section className="card">
              <h2>Afbud på holdet</h2>
              <p className="tiny muted mt-1">
                Afbud er en besked fra familien. Det sætter ikke automatisk personen som fraværende i
                fremmøderegistreringen.
              </p>
              {data.absences.length === 0 ? (
                <EmptyState title="Ingen afbud">Der er ikke meldt afbud til dette hold.</EmptyState>
              ) : (
                <ul className="list mt-3">
                  {data.absences.map((a) => (
                    <li key={a.id} className="row row--between">
                      <span className="small">{a.personName}</span>
                      <span className="tiny muted">
                        {a.date} · {a.reasonLabel}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            )}

            {data.team.kind === 'membership' ? null : (
              <ExtraSessionForm team={data.team} onCreated={state.reload} />
            )}

            <MessageComposer
              teamId={data.team.teamId}
              teamName={data.team.name}
              teamKind={data.team.kind}
            />
          </>
        )}
      </AsyncSection>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Aflys, genåbn og flyt                                               */
/* ------------------------------------------------------------------ */

/**
 * Instruktørens vigtigste knap.
 *
 * En aflysning skal kunne meldes fra hallen på 15 sekunder med én hånd —
 * ellers ender den på Facebook i stedet, og så ser halvdelen den ikke.
 * Derfor: én knap, én begrundelse, og en tydelig besked om, at der bliver
 * sendt en notifikation med det samme.
 */
function SessionControls({
  session,
  onChanged,
}: {
  session: TeamSession;
  onChanged: () => void;
}): JSX.Element {
  const [mode, setMode] = useState<'none' | 'cancel' | 'move'>('none');
  const [reason, setReason] = useState('');
  const [toDate, setToDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const action = useAction();

  const cancel = (): void => {
    void action.run(async () => {
      const result = await api.post<{ notification: { sent: number; recipients: number } | null }>(
        `/api/sessions/${encodeURIComponent(session.id)}/cancel`,
        { reason },
      );
      action.setSuccess(
        `Træningen er aflyst. ${describeNotification(result.notification)}`,
      );
      setMode('none');
      setReason('');
      onChanged();
    });
  };

  const reopen = (): void => {
    void action.run(async () => {
      const result = await api.post<{ note: string }>(
        `/api/sessions/${encodeURIComponent(session.id)}/reopen`,
      );
      action.setSuccess(result.note);
      onChanged();
    });
  };

  const move = (): void => {
    void action.run(async () => {
      const result = await api.post<{ notification: { sent: number; recipients: number } | null }>(
        `/api/sessions/${encodeURIComponent(session.id)}/move`,
        {
          toDate,
          startTime: startTime || undefined,
          endTime: endTime || undefined,
          reason,
        },
      );
      action.setSuccess(`Træningen er flyttet. ${describeNotification(result.notification)}`);
      setMode('none');
      setReason('');
      setToDate('');
      onChanged();
    });
  };

  return (
    <div className="mt-3">
      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      {session.status !== 'scheduled' ? (
        <div className="row row--between mt-2">
          <div>
            <Badge kind={session.status === 'closed' ? 'muted' : 'warning'}>
              {session.status === 'cancelled'
                ? 'Aflyst'
                : session.status === 'moved'
                  ? 'Flyttet'
                  : 'Ferielukket'}
            </Badge>
            {session.reason ? (
              <p className="tiny muted mt-1">
                {session.reason}
                {session.changedByName ? ` — ${session.changedByName}` : ''}
              </p>
            ) : null}
          </div>
          {session.status === 'cancelled' || session.status === 'moved' ? (
            <ActionButton
              className="btn btn--secondary btn--small"
              pending={action.pending}
              onClick={reopen}
            >
              Fortryd
            </ActionButton>
          ) : null}
        </div>
      ) : mode === 'none' ? (
        <div className="btn-group mt-2">
          <button type="button" className="btn btn--danger" onClick={() => setMode('cancel')}>
            Aflys træningen
          </button>
          <button type="button" className="btn btn--secondary" onClick={() => setMode('move')}>
            Flyt træningen
          </button>
        </div>
      ) : mode === 'cancel' ? (
        <div className="stack stack--tight mt-3">
          <Field
            label="Begrundelse"
            htmlFor="cancel-reason"
            hint="Står ordret i kalenderen og i notifikationen. «Instruktøren er syg» er nok."
          >
            <input
              id="cancel-reason"
              type="text"
              maxLength={200}
              value={reason}
              onChange={(e) => setReason((e.target as HTMLInputElement).value)}
            />
          </Field>
          <Notice kind="warning">
            Alle familier på holdet får besked med det samme — også dem, der allerede har meldt
            afbud. Det kan ikke fortrydes for dem, der har fået notifikationen.
          </Notice>
          <div className="btn-group">
            <ActionButton className="btn btn--danger" pending={action.pending} onClick={cancel}>
              Aflys og send besked
            </ActionButton>
            <button type="button" className="btn btn--secondary" onClick={() => setMode('none')}>
              Fortryd
            </button>
          </div>
        </div>
      ) : (
        <div className="stack stack--tight mt-3">
          <Field label="Ny dato" htmlFor="move-date">
            <input
              id="move-date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate((e.target as HTMLInputElement).value)}
            />
          </Field>
          <div className="row" style={{ gap: 10 }}>
            <Field label="Ny starttid (valgfri)" htmlFor="move-start">
              <input
                id="move-start"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime((e.target as HTMLInputElement).value)}
              />
            </Field>
            <Field label="Ny sluttid (valgfri)" htmlFor="move-end">
              <input
                id="move-end"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime((e.target as HTMLInputElement).value)}
              />
            </Field>
          </div>
          <Field label="Begrundelse" htmlFor="move-reason">
            <input
              id="move-reason"
              type="text"
              maxLength={200}
              value={reason}
              onChange={(e) => setReason((e.target as HTMLInputElement).value)}
            />
          </Field>
          <div className="btn-group">
            <ActionButton pending={action.pending} onClick={move} disabled={!toDate}>
              Flyt og send besked
            </ActionButton>
            <button type="button" className="btn btn--secondary" onClick={() => setMode('none')}>
              Fortryd
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Ærlig formulering: accepteret af pushtjenesten er ikke «læst». */
function describeNotification(n: { sent: number; recipients: number } | null): string {
  if (!n) return 'Der blev ikke sendt en notifikation.';
  if (n.recipients === 0) return 'Ingen familier er tilmeldt holdet endnu.';
  return `Notifikationen blev sendt til ${n.sent} af ${n.recipients} modtagere — det er ikke en kvittering for, at den er læst. Aflysningen står under alle omstændigheder i kalenderen.`;
}

/* ------------------------------------------------------------------ */
/* Ekstra træning                                                      */
/* ------------------------------------------------------------------ */

function ExtraSessionForm({
  team,
  onCreated,
}: {
  team: TeamListItem;
  onCreated: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  // Et medlemskab har ingen tider at foreslå. Formularen står tom, og
  // instruktøren skriver dem selv.
  const [startTime, setStartTime] = useState(team.startTime ?? '');
  const [endTime, setEndTime] = useState(team.endTime ?? '');
  const [reason, setReason] = useState('');
  const action = useAction();

  const create = (): void => {
    void action.run(async () => {
      await api.post('/api/sessions', {
        teamId: team.teamId,
        date,
        startTime,
        endTime,
        reason,
      });
      action.setSuccess('Den ekstra træning er lagt i kalenderen.');
      setOpen(false);
      setDate('');
      setReason('');
      onCreated();
    });
  };

  return (
    <section className="card">
      <div className="card__header">
        <h2>Ekstra træning</h2>
        {!open ? (
          <button type="button" className="btn btn--secondary btn--small" onClick={() => setOpen(true)}>
            Tilføj
          </button>
        ) : null}
      </div>
      <p className="tiny muted mt-1">
        Til opvisningsprøver, ekstra træning før stævner og lignende. Den lægges i familiernes
        kalender som en almindelig træning — men markeret som ekstra.
      </p>

      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      {open ? (
        <div className="stack stack--tight mt-3">
          <Field label="Dato" htmlFor="extra-date">
            <input
              id="extra-date"
              type="date"
              value={date}
              onChange={(e) => setDate((e.target as HTMLInputElement).value)}
            />
          </Field>
          <div className="row" style={{ gap: 10 }}>
            <Field label="Starttid" htmlFor="extra-start">
              <input
                id="extra-start"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime((e.target as HTMLInputElement).value)}
              />
            </Field>
            <Field label="Sluttid" htmlFor="extra-end">
              <input
                id="extra-end"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime((e.target as HTMLInputElement).value)}
              />
            </Field>
          </div>
          <Field label="Anledning" htmlFor="extra-reason" hint="Fx «Generalprøve til opvisning».">
            <input
              id="extra-reason"
              type="text"
              maxLength={200}
              value={reason}
              onChange={(e) => setReason((e.target as HTMLInputElement).value)}
            />
          </Field>
          <div className="btn-group">
            <ActionButton pending={action.pending} onClick={create} disabled={!date}>
              Læg i kalenderen
            </ActionButton>
            <button type="button" className="btn btn--secondary" onClick={() => setOpen(false)}>
              Fortryd
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Fremmøde                                                            */
/* ------------------------------------------------------------------ */

function AttendanceEditor({
  sessionId,
  sheet,
  onSaved,
}: {
  sessionId: string;
  sheet: AttendanceSheet;
  onSaved: () => void;
}): JSX.Element {
  const [entries, setEntries] = useState<AttendanceEntry[]>(sheet.entries);
  const [dirty, setDirty] = useState(false);
  const action = useAction();

  // Nulstil, når serverdata skifter (ny træning eller genindlæsning).
  const signature = `${sheet.sessionId}:${sheet.entries.map((e) => `${e.memberId}${e.status}`).join('|')}`;
  const [lastSignature, setLastSignature] = useState(signature);
  if (signature !== lastSignature) {
    setLastSignature(signature);
    setEntries(sheet.entries);
    setDirty(false);
  }

  if (sheet.entries.length === 0) {
    return (
      <EmptyState title="Ingen deltagere på holdet">
        Holdet har ingen registrerede deltagere i Conventus. Instruktører og hjælpere tælles ikke som
        deltagere.
      </EmptyState>
    );
  }

  const setStatus = (memberId: string, status: 'present' | 'absent' | null): void => {
    setEntries((prev) => prev.map((e) => (e.memberId === memberId ? { ...e, status } : e)));
    setDirty(true);
  };

  const setNote = (memberId: string, note: string): void => {
    setEntries((prev) => prev.map((e) => (e.memberId === memberId ? { ...e, note } : e)));
    setDirty(true);
  };

  const save = (): void => {
    void action.run(async () => {
      await api.post('/api/attendance', {
        sessionId,
        entries: entries.map((e) => ({ memberId: e.memberId, status: e.status, note: e.note })),
      });
      action.setSuccess('Fremmødet er gemt.');
      setDirty(false);
      onSaved();
    });
  };

  const allPresent = (): void => {
    void action.run(async () => {
      await api.post('/api/attendance/all-present', { sessionId });
      action.setSuccess('Alle er markeret til stede.');
      setDirty(false);
      onSaved();
    });
  };

  const counts = {
    present: entries.filter((e) => e.status === 'present').length,
    absent: entries.filter((e) => e.status === 'absent').length,
    unregistered: entries.filter((e) => e.status === null).length,
  };

  return (
    <div className="mt-3">
      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      {sheet.absences.length > 0 ? (
        <div className="mt-3">
          <Notice kind="info">
            <strong>Meldt afbud:</strong>{' '}
            {sheet.absences.map((a) => `${a.personName} (${a.reasonLabel})`).join(', ')}
          </Notice>
        </div>
      ) : null}

      <div className="row" style={{ gap: 6, margin: '10px 0' }}>
        <Badge kind="ok">{counts.present} til stede</Badge>
        <Badge kind="warning">{counts.absent} fraværende</Badge>
        <Badge>{counts.unregistered} ikke registreret</Badge>
      </div>

      <div>
        {entries.map((entry) => (
          <div key={entry.memberId} className="attendance-row">
            <div style={{ flex: '1 1 160px' }}>
              <div className="small">
                <strong>{entry.name}</strong>
              </div>
              <input
                type="text"
                className="tiny"
                placeholder="Note (valgfri, maks. 160 tegn)"
                maxLength={160}
                value={entry.note}
                onChange={(e) => setNote(entry.memberId, (e.target as HTMLInputElement).value)}
                style={{ marginTop: 4, minHeight: 44, fontSize: 13 }}
                aria-label={`Note for ${entry.name}`}
              />
            </div>
            <div className="seg" role="group" aria-label={`Fremmøde for ${entry.name}`}>
              <button
                type="button"
                className="seg__present"
                aria-pressed={entry.status === 'present'}
                onClick={() => setStatus(entry.memberId, 'present')}
              >
                Til stede
              </button>
              <button
                type="button"
                className="seg__absent"
                aria-pressed={entry.status === 'absent'}
                onClick={() => setStatus(entry.memberId, 'absent')}
              >
                Fraværende
              </button>
              <button
                type="button"
                aria-pressed={entry.status === null}
                onClick={() => setStatus(entry.memberId, null)}
              >
                Nulstil
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="btn-group mt-4">
        <ActionButton pending={action.pending} onClick={save} disabled={!dirty}>
          Gem fremmøde
        </ActionButton>
        <ActionButton className="btn btn--secondary" pending={action.pending} onClick={allPresent}>
          Markér alle til stede
        </ActionButton>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Beskedformular                                                      */
/* ------------------------------------------------------------------ */

interface RecipientData {
  users: Array<{ userId: string; name: string; reason: string }>;
  teamTrainers: Array<{ userId: string; name: string }>;
  trainerGroupNote: string;
  allowedDepartments: string[];
  canSendToAll: boolean;
}

export function MessageComposer({
  teamId,
  teamName,
  teamKind = 'training',
}: {
  teamId?: string;
  teamName?: string;
  /** Et medlemskab er ikke «holdet». Ordet i målgruppen skal passe. */
  teamKind?: 'training' | 'membership';
}): JSX.Element {
  const recipients = useAsync<RecipientData>(
    () => api.get(`/api/announcement-recipients${teamId ? `?teamId=${encodeURIComponent(teamId)}` : ''}`),
    [teamId],
  );
  const [audience, setAudience] = useState(teamId ? 'team' : 'department');
  const [department, setDepartment] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [push, setPush] = useState(false);
  // Svar er slået til, hvor en samtale giver mening. En udmelding til hele
  // foreningen er ikke en samtale — dér skal man vælge det aktivt.
  const [allowReplies, setAllowReplies] = useState(repliesEnabledByDefault('team'));
  const action = useAction();

  const changeAudience = (next: string): void => {
    setAudience(next);
    setAllowReplies(repliesEnabledByDefault(next as Audience));
  };

  const submit = (): void => {
    void action.run(async () => {
      const result = await api.post<{
        push: { accepted: number; attempted: number; note: string } | null;
        pushSkippedReason: string | null;
      }>('/api/announcements', {
        title,
        body,
        audience,
        priority,
        push,
        allowReplies,
        department: department || undefined,
        teamId: teamId || undefined,
        targetUserId: targetUserId || undefined,
      });
      const pushText = result.pushSkippedReason
        ? ` ${result.pushSkippedReason}`
        : result.push
          ? ` Pushtjenesten accepterede ${result.push.accepted} af ${result.push.attempted} forsøg — det er ikke en kvittering for, at beskeden er læst.`
          : '';
      action.setSuccess(`Beskeden er sendt.${pushText}`);
      setTitle('');
      setBody('');
    });
  };

  return (
    <section className="card">
      <h2>Send besked</h2>
      <AsyncSection state={recipients} skeletonLines={3}>
        {(data) => (
          <div className="stack stack--tight mt-3">
            {action.error ? <Notice kind="error">{action.error}</Notice> : null}
            {action.success ? <Notice kind="success">{action.success}</Notice> : null}

            <Field label="Målgruppe" htmlFor="msg-audience">
              <select
                id="msg-audience"
                value={audience}
                onChange={(e) => changeAudience((e.target as HTMLSelectElement).value)}
              >
                {data.canSendToAll ? <option value="all">Hele foreningen</option> : null}
                {data.allowedDepartments.length > 0 ? (
                  <option value="department">En afdeling</option>
                ) : null}
                {teamId ? (
                  <option value="team">
                    {teamKind === 'membership' ? 'Medlemskabet' : 'Holdet'} {teamName}
                  </option>
                ) : null}
                {teamId ? <option value="team_trainers">Holdets instruktører</option> : null}
                <option value="user">Én person (privat)</option>
              </select>
            </Field>

            {/*
              HVEM DEN NÅR, STÅR VED VALGET — ikke i en hjælpetekst et andet
              sted. Målt mod serveren: en afdelingsbesked går til alle med et
              medlem på et hold i afdelingen, til afdelingens instruktører og
              udvalg, OG til hele bestyrelsen.

              Det sidste er det, ingen ville gætte. Skriver et udvalgsmedlem
              «husk badehætte», ryger den også i indbakken hos de fem i
              bestyrelsen. Det er efter hensigten — bestyrelsen skal kunne
              følge med — men man skal vide det, før man trykker Send, ikke
              opdage det bagefter.
            */}
            {audience === 'department' ? (
              <Field
                label="Afdeling"
                htmlFor="msg-department"
                hint="Går til alle med et medlem på et hold i afdelingen, til afdelingens instruktører og udvalg — og til bestyrelsen."
                error={action.fieldError('department')}
              >
                <select
                  id="msg-department"
                  value={department}
                  onChange={(e) => setDepartment((e.target as HTMLSelectElement).value)}
                >
                  <option value="">Vælg afdeling</option>
                  {data.allowedDepartments.map((d) => (
                    <option key={d} value={d}>
                      {departmentLabel(d)}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}

            {audience === 'user' ? (
              <Field
                label="Modtager"
                htmlFor="msg-user"
                hint="Kun registrerede appkonti. En løs mailadresse kan ikke vælges."
                error={action.fieldError('targetUserId')}
              >
                <select
                  id="msg-user"
                  value={targetUserId}
                  onChange={(e) => setTargetUserId((e.target as HTMLSelectElement).value)}
                >
                  <option value="">Vælg modtager</option>
                  {data.users.map((u) => (
                    <option key={u.userId} value={u.userId}>
                      {u.name} — {u.reason}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}

            {audience === 'team_trainers' ? (
              <Notice kind="info">
                <strong>Modtagere lige nu:</strong>{' '}
                {data.teamTrainers.length === 0
                  ? 'ingen appkonti har en instruktørtildeling til dette hold.'
                  : data.teamTrainers.map((t) => t.name).join(', ')}
                <p className="tiny mt-2">
                  {data.trainerGroupNote}
                </p>
              </Notice>
            ) : null}

            <Field
              label="Overskrift"
              htmlFor="msg-title"
              hint={`${title.length}/100 tegn`}
              error={action.fieldError('title')}
            >
              <input
                id="msg-title"
                type="text"
                maxLength={100}
                value={title}
                onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
              />
            </Field>

            <Field
              label="Tekst"
              htmlFor="msg-body"
              hint={`${body.length}/800 tegn`}
              error={action.fieldError('body')}
            >
              <textarea
                id="msg-body"
                maxLength={800}
                value={body}
                onChange={(e) => setBody((e.target as HTMLTextAreaElement).value)}
              />
            </Field>

            <Field label="Prioritet" htmlFor="msg-priority">
              <select
                id="msg-priority"
                value={priority}
                onChange={(e) => setPriority((e.target as HTMLSelectElement).value)}
              >
                <option value="normal">Normal</option>
                <option value="important">Vigtig</option>
              </select>
            </Field>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={allowReplies}
                onChange={(e) => setAllowReplies((e.target as HTMLInputElement).checked)}
              />
              <span className="small">
                Modtagerne må svare dig
                <span className="tiny muted" style={{ display: 'block' }}>
                  Et svar går kun til dig — ingen andre modtagere kan se det. Slå det fra på
                  udmeldinger, hvor du ikke vil have svar tilbage.
                </span>
              </span>
            </label>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={push}
                onChange={(e) => setPush((e.target as HTMLInputElement).checked)}
              />
              <span className="small">
                Forsøg at sende som pushnotifikation
                <span className="tiny muted" style={{ display: 'block' }}>
                  Kun til modtagere, der har slået notifikationer til og ikke har fravalgt
                  kategorien.
                </span>
              </span>
            </label>

            <ActionButton
              pending={action.pending}
              onClick={submit}
              disabled={!title.trim() || !body.trim()}
            >
              Send besked
            </ActionButton>
          </div>
        )}
      </AsyncSection>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Arrangementer                                                       */
/* ------------------------------------------------------------------ */

interface EventsData {
  events: Array<{
    id: string;
    title: string;
    description: string;
    location: string;
    startsAt: string;
    endsAt: string | null;
    allDay: boolean;
    audience: 'all' | 'department' | 'team';
    audienceLabel: string;
    status: 'scheduled' | 'cancelled';
    cancelReason: string;
    createdByName: string;
    helperSlots: number;
    helpersNeeded: number;
    signupEnabled: boolean;
    signupDeadline: string | null;
    priceOere: number;
    paymentUrl: string;
    shifts: Array<{
      id: string;
      title: string;
      startsAt: string;
      capacity: number;
      signedUp: number;
      participants: string[];
    }>;
  }>;
  allowedDepartments: string[];
  canCreateForAll: boolean;
  teams: Array<{ teamId: string; name: string }>;
}

/**
 * ARRANGEMENTER — det, foreningen samles om.
 *
 * Opvisning, generalforsamling, stævne, sommerfest. Det står ikke i
 * Conventus og kan ikke hentes nogen steder fra; det tastes ind her og
 * lander i familiernes kalender ved siden af træningerne.
 *
 * Dato og klokkeslæt er to felter og ikke ét. Et kombineret felt tvinger
 * folk til at kende et format, og den, der taster foreningens opvisning ind,
 * skal ikke først lære, hvad ISO 8601 er.
 */
export function EventsPage(): JSX.Element {
  const state = useAsync<EventsData>(() => api.get('/api/events'));
  const action = useAction();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [audience, setAudience] = useState('all');
  const [department, setDepartment] = useState('');
  const [teamId, setTeamId] = useState('');
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [signupDeadline, setSignupDeadline] = useState('');
  const [priceKroner, setPriceKroner] = useState('');
  const [paymentUrl, setPaymentUrl] = useState('');
  const [redigerer, setRedigerer] = useState<string | null>(null);

  const nulstil = (): void => {
    setTitle('');
    setDescription('');
    setLocation('');
    setDate('');
    setStartTime('');
    setEndTime('');
    setAllDay(false);
    setSignupEnabled(false);
    setSignupDeadline('');
    setPriceKroner('');
    setPaymentUrl('');
    setRedigerer(null);
  };

  const gem = (): void => {
    void action.run(async () => {
      const krop = {
        title,
        description,
        location,
        date,
        startTime,
        endTime,
        allDay,
        audience,
        department: audience === 'department' ? department : undefined,
        teamId: audience === 'team' ? teamId : undefined,
        signupEnabled,
        signupDeadline: signupEnabled ? signupDeadline || undefined : undefined,
        priceKroner: signupEnabled ? priceKroner || undefined : undefined,
        paymentUrl: signupEnabled ? paymentUrl || undefined : undefined,
      };
      if (redigerer) {
        await api.put(`/api/events/${encodeURIComponent(redigerer)}`, krop);
        action.setSuccess('Arrangementet er rettet.');
      } else {
        await api.post('/api/events', krop);
        action.setSuccess('Arrangementet står nu i kalenderen hos dem, det gælder.');
      }
      nulstil();
      state.reload();
    });
  };

  return (
    <div className="stack">
      <PageHead
        title="Arrangementer"
        eyebrow="Foreningen"
        eyebrowIcon="calendar"
        sub="Opvisning, generalforsamling, stævne — det, der ikke er ugentlig træning"
      />

      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      <AsyncSection state={state} skeletonLines={4}>
        {(data) => (
          <>
            <section className="card stack stack--tight">
              <h2>{redigerer ? 'Ret arrangement' : 'Nyt arrangement'}</h2>

              <Field label="Overskrift" htmlFor="ev-title" error={action.fieldError('title')}>
                <input
                  id="ev-title"
                  type="text"
                  maxLength={100}
                  placeholder="Forårsopvisning"
                  value={title}
                  onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
                />
              </Field>

              <Field
                label="Dato"
                htmlFor="ev-date"
                hint="Vælg dagen. Er det over flere dage, opret ét arrangement pr. dag — så står de hver for sig i kalenderen."
                error={action.fieldError('date')}
              >
                <input
                  id="ev-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate((e.target as HTMLInputElement).value)}
                />
              </Field>

              <label className="row" style={{ gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={allDay}
                  onChange={(e) => setAllDay((e.target as HTMLInputElement).checked)}
                />
                <span className="small">Hele dagen — intet klokkeslæt</span>
              </label>

              {/*
                Klokkeslættet skjules, når det er hele dagen. Et felt, der
                ikke betyder noget, bliver alligevel udfyldt af nogen — og så
                står der et tidspunkt i kalenderen, som ingen har ment.
              */}
              {allDay ? null : (
                <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                  <Field label="Fra kl." htmlFor="ev-start" error={action.fieldError('startTime')}>
                    <input
                      id="ev-start"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime((e.target as HTMLInputElement).value)}
                    />
                  </Field>
                  <Field label="Til kl. (valgfrit)" htmlFor="ev-end" error={action.fieldError('endTime')}>
                    <input
                      id="ev-end"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime((e.target as HTMLInputElement).value)}
                    />
                  </Field>
                </div>
              )}

              <Field label="Sted (valgfrit)" htmlFor="ev-loc">
                <input
                  id="ev-loc"
                  type="text"
                  maxLength={120}
                  placeholder="Skals Hallen"
                  value={location}
                  onChange={(e) => setLocation((e.target as HTMLInputElement).value)}
                />
              </Field>

              <Field
                label="Målgruppe"
                htmlFor="ev-audience"
                hint="Arrangementet dukker op i kalenderen hos dem, det gælder — og kun dem."
              >
                <select
                  id="ev-audience"
                  value={audience}
                  onChange={(e) => setAudience((e.target as HTMLSelectElement).value)}
                >
                  {/*
                    Vælgeren bygges på serverens lister, ikke på et gæt. Så
                    kan der aldrig stå en mulighed, serveren vil afvise.
                  */}
                  {data.canCreateForAll ? <option value="all">Hele foreningen</option> : null}
                  {data.allowedDepartments.length > 0 ? (
                    <option value="department">En afdeling</option>
                  ) : null}
                  {data.teams.length > 0 ? <option value="team">Et hold</option> : null}
                </select>
              </Field>

              {audience === 'department' ? (
                <Field label="Afdeling" htmlFor="ev-dept" error={action.fieldError('department')}>
                  <select
                    id="ev-dept"
                    value={department}
                    onChange={(e) => setDepartment((e.target as HTMLSelectElement).value)}
                  >
                    <option value="">Vælg afdeling</option>
                    {data.allowedDepartments.map((d) => (
                      <option key={d} value={d}>
                        {departmentLabel(d)}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : null}

              {audience === 'team' ? (
                <Field label="Hold" htmlFor="ev-team" error={action.fieldError('teamId')}>
                  <select
                    id="ev-team"
                    value={teamId}
                    onChange={(e) => setTeamId((e.target as HTMLSelectElement).value)}
                  >
                    <option value="">Vælg hold</option>
                    {data.teams.map((t) => (
                      <option key={t.teamId} value={t.teamId}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : null}

              <Field
                label="Beskrivelse (valgfrit)"
                htmlFor="ev-desc"
                hint="Det, familierne har brug for at vide. Hvad koster det, skal der medbringes noget, hvornår må man gå."
              >
                <textarea
                  id="ev-desc"
                  rows={4}
                  maxLength={2000}
                  value={description}
                  onChange={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
                />
              </Field>

              {/*
                TILMELDINGEN ER VALGFRI. Er den slået fra, opfører
                arrangementet sig præcis som før — ingen knap, ingen liste,
                ingen frist. De fleste arrangementer skal ikke have den.
              */}
              <label className="row" style={{ gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={signupEnabled}
                  onChange={(e) => setSignupEnabled((e.target as HTMLInputElement).checked)}
                />
                <span className="small">Familierne skal melde til</span>
              </label>

              {signupEnabled ? (
                <div className="stack stack--tight">
                  <Field
                    label="Tilmeldingsfrist (valgfrit)"
                    htmlFor="ev-frist"
                    hint="Uden en frist løber tilmeldingen, til arrangementet begynder."
                    error={action.fieldError('signupDeadline')}
                  >
                    <input
                      id="ev-frist"
                      type="date"
                      value={signupDeadline}
                      onChange={(e) => setSignupDeadline((e.target as HTMLInputElement).value)}
                    />
                  </Field>

                  <Field
                    label="Pris pr. deltager i kroner (valgfrit)"
                    htmlFor="ev-pris"
                    hint="Lad feltet stå tomt, hvis arrangementet er gratis."
                    error={action.fieldError('priceKroner')}
                  >
                    <input
                      id="ev-pris"
                      type="text"
                      inputMode="decimal"
                      maxLength={10}
                      placeholder="50"
                      value={priceKroner}
                      onChange={(e) => setPriceKroner((e.target as HTMLInputElement).value)}
                    />
                  </Field>

                  {priceKroner ? (
                    <Field
                      label="Betalingslink fra MobilePay"
                      htmlFor="ev-link"
                      hint="Opret et betalingslink med beløbet i MobilePay, og indsæt adressen her."
                      error={action.fieldError('paymentUrl')}
                    >
                      <input
                        id="ev-link"
                        type="url"
                        maxLength={300}
                        placeholder="https://..."
                        value={paymentUrl}
                        onChange={(e) => setPaymentUrl((e.target as HTMLInputElement).value)}
                      />
                    </Field>
                  ) : null}

                  {/*
                    DET HER ER DEN VIGTIGSTE SÆTNING PÅ SIDEN. Appen får
                    aldrig besked fra MobilePay. Tror arrangøren, at listen
                    opdaterer sig selv, går hun fra et arrangement i den tro,
                    at alle har betalt.
                  */}
                  {priceKroner ? (
                    <Notice kind="warning">
                      <strong>Appen kan ikke se, om der er betalt.</strong>
                      <p className="small mt-1">
                        Linket sender familien til MobilePay, og dér slutter appens viden. Du
                        krydser selv af på tilmeldingslisten, når du kan se betalingen i
                        MobilePay. Skriv i beskrivelsen, at de skal skrive deltagerens navn i
                        beskeden — ellers står du med betalinger, du ikke kan placere.
                      </p>
                    </Notice>
                  ) : null}
                </div>
              ) : null}

              <div className="btn-group">
                <ActionButton className="btn" pending={action.pending} onClick={gem}>
                  {redigerer ? 'Gem ændringen' : 'Læg i kalenderen'}
                </ActionButton>
                {redigerer ? (
                  <button type="button" className="btn btn--secondary" onClick={nulstil}>
                    Fortryd
                  </button>
                ) : null}
              </div>

              {/*
                Det siges HER, hvad knappen ikke gør. Den lægger noget i
                kalenderen; den sender ikke noget ud. Uden den linje ville
                nogen tro, at foreningen var underrettet, og opdage det for
                sent.
              */}
              <p className="tiny muted">
                Arrangementet dukker op i kalenderen, men der bliver ikke sendt en besked ud om
                det. Skal familierne have besked med det samme, så send den under «Beskeder».
              </p>
            </section>

            <section className="section" aria-labelledby="ev-liste">
              <SectionHead title="Kommende og tidligere" icon="calendar" id="ev-liste" />
              <div className="card">
                {data.events.length === 0 ? (
                  <EmptyState title="Ingen arrangementer endnu" icon="calendar">
                    Når du opretter et, står det her — og i kalenderen hos dem, det gælder.
                  </EmptyState>
                ) : (
                  <ul className="rows">
                    {data.events.map((ev) => (
                      <EventRow
                        key={ev.id}
                        event={ev}
                        onChanged={state.reload}
                        onEdit={() => {
                          setRedigerer(ev.id);
                          setTitle(ev.title);
                          setDescription(ev.description);
                          setLocation(ev.location);
                          setDate(ev.startsAt.slice(0, 10));
                          setAllDay(ev.allDay);
                          setAudience(ev.audience);
                          /*
                            Tilmeldingsfelterne skal MED ind i formularen.
                            Gjorde de ikke det, ville en rettelse af
                            overskriften slå tilmeldingen fra og nulstille
                            prisen — og serveren ville afvise det med en fejl,
                            der så ud til at komme fra ingenting.
                          */
                          setSignupEnabled(ev.signupEnabled);
                          setSignupDeadline(ev.signupDeadline ? ev.signupDeadline.slice(0, 10) : '');
                          setPriceKroner(ev.priceOere > 0 ? String(ev.priceOere / 100) : '');
                          setPaymentUrl(ev.paymentUrl);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      />
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </>
        )}
      </AsyncSection>
    </div>
  );
}

/**
 * En række i listen — med aflysning og fjernelse som TO forskellige ting.
 *
 * Aflys det, der har været meldt ud: så bliver det stående i familiernes
 * kalender med en begrundelse. Fjern det, der blev oprettet ved en fejl og
 * aldrig har betydet noget for nogen. Havde vi kun én knap, ville folk
 * bruge den forkerte, og en aflysning ville se ud som om arrangementet
 * aldrig havde eksisteret.
 */
function EventRow({
  event,
  onChanged,
  onEdit,
}: {
  event: EventsData['events'][number];
  onChanged: () => void;
  onEdit: () => void;
}): JSX.Element {
  const action = useAction();
  const [aflyser, setAflyser] = useState(false);
  const [begrundelse, setBegrundelse] = useState('');
  const aflyst = event.status === 'cancelled';

  const aflys = (): void => {
    void action.run(async () => {
      await api.post(`/api/events/${encodeURIComponent(event.id)}/aflys`, { reason: begrundelse });
      setAflyser(false);
      setBegrundelse('');
      onChanged();
    });
  };

  const fjern = (): void => {
    void action.run(async () => {
      await api.del(`/api/events/${encodeURIComponent(event.id)}`);
      onChanged();
    });
  };

  return (
    <li>
      <div className="row row--between">
        <span className="small">
          <strong style={aflyst ? { textDecoration: 'line-through' } : undefined}>{event.title}</strong>
        </span>
        {aflyst ? <Badge kind="warning">Aflyst</Badge> : null}
      </div>
      <p className="tiny muted">
        {formatDateTime(event.startsAt)} · {event.audienceLabel}
        {event.location ? ` · ${event.location}` : ''}
      </p>
      {aflyst ? <p className="tiny">Aflyst: {event.cancelReason}</p> : null}

      <HelpersForEvent event={event} onChanged={onChanged} />

      {action.error ? (
        <div className="mt-2">
          <Notice kind="error">{action.error}</Notice>
        </div>
      ) : null}

      {aflyser ? (
        <div className="mt-2">
          <Field
            label="Hvorfor aflyses det?"
            htmlFor={`ev-why-${event.id}`}
            hint="Det er dét, familierne læser i kalenderen. «Hallen er optaget» er nok."
          >
            <input
              id={`ev-why-${event.id}`}
              type="text"
              maxLength={200}
              value={begrundelse}
              onChange={(e) => setBegrundelse((e.target as HTMLInputElement).value)}
            />
          </Field>
          <div className="btn-group mt-2">
            <ActionButton className="btn btn--danger btn--small" pending={action.pending} onClick={aflys}>
              Aflys arrangementet
            </ActionButton>
            <button type="button" className="btn btn--secondary btn--small" onClick={() => setAflyser(false)}>
              Fortryd
            </button>
          </div>
        </div>
      ) : (
        <p className="row mt-2" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn--ghost btn--small" onClick={onEdit}>
            Ret
          </button>
          {aflyst ? null : (
            <button type="button" className="btn btn--ghost btn--small" onClick={() => setAflyser(true)}>
              Aflys
            </button>
          )}
          <ActionButton className="btn btn--ghost btn--small" pending={action.pending} onClick={fjern}>
            Fjern helt
          </ActionButton>
        </p>
      )}

      {event.signupEnabled ? <TilmeldingsListe event={event} /> : null}
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Arrangørens tilmeldingsliste                                        */
/* ------------------------------------------------------------------ */

interface OrganiserListe {
  priceOere: number;
  paymentUrl: string;
  signups: Array<{
    id: string;
    userName: string;
    adults: number;
    people: Array<{ memberId: string; name: string }>;
    paidAt: string | null;
    paidByName: string;
  }>;
  summary: {
    tilmeldinger: number;
    deltagere: number;
    voksne: number;
    ialt: number;
    betalt: number;
    mangler: number;
  };
}

function kroner(oere: number): string {
  return oere % 100 === 0 ? `${oere / 100} kr.` : `${(oere / 100).toFixed(2).replace('.', ',')} kr.`;
}

/**
 * Hvem der kommer — og hvem der mangler at betale.
 *
 * Listen findes for ÉN opgave: at sidde med MobilePay-oversigten og finde de
 * manglende. Derfor står de ubetalte øverst (det gør serveren), og derfor er
 * krydset det eneste, man kan gøre her.
 *
 * Foldet sammen som udgangspunkt, ligesom hjælperne. Navnene på andres børn
 * skal ikke ligge åbent på en side, man skimmer.
 */
function TilmeldingsListe({ event }: { event: EventsData['events'][number] }): JSX.Element {
  const [aaben, setAaben] = useState(false);
  const state = useAsync<OrganiserListe>(() =>
    api.get(`/api/events/${encodeURIComponent(event.id)}/tilmeldinger`),
  );
  const action = useAction();

  const saetKryds = (signupId: string, paid: boolean): void => {
    void action.run(async () => {
      await api.post(
        `/api/events/${encodeURIComponent(event.id)}/tilmeldinger/${encodeURIComponent(signupId)}/betalt`,
        { paid },
      );
      state.reload();
    });
  };

  if (!aaben) {
    return (
      <p className="mt-2">
        <button type="button" className="btn btn--ghost btn--small" onClick={() => setAaben(true)}>
          Se tilmeldinger
        </button>
      </p>
    );
  }

  return (
    <div className="mt-2">
      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      <AsyncSection state={state} skeletonLines={2}>
        {(data) => (
          <>
            <p className="small">
              <strong>{antal(data.summary.ialt, 'deltager', 'deltagere')}</strong>{' '}
              <span className="muted">
                ({data.summary.deltagere} fra holdene, {data.summary.voksne} voksne ·{' '}
                {antal(data.summary.tilmeldinger, 'tilmelding', 'tilmeldinger')})
              </span>
            </p>

            {data.priceOere > 0 ? (
              <p className="small">
                {data.summary.mangler > 0 ? (
                  <strong>
                    {antal(data.summary.mangler, 'mangler', 'mangler')} at betale ·{' '}
                    {kroner(data.summary.mangler * data.priceOere)}
                  </strong>
                ) : (
                  <span>Alle har betalt.</span>
                )}
              </p>
            ) : null}

            {data.signups.length === 0 ? (
              <p className="tiny muted">Ingen har meldt sig endnu.</p>
            ) : (
              <ul className="list">
                {data.signups.map((t) => (
                  <li key={t.id} className="row row--between">
                    <span className="small">
                      <strong>{t.userName || 'Slettet konto'}</strong>
                      {t.people.length > 0 ? (
                        <span className="muted"> · {t.people.map((p) => p.name).join(', ')}</span>
                      ) : null}
                      {t.adults > 0 ? (
                        <span className="muted"> · {antal(t.adults, 'voksen', 'voksne')}</span>
                      ) : null}
                      {t.paidAt ? (
                        <span className="tiny muted"> · afkrydset af {t.paidByName}</span>
                      ) : null}
                    </span>
                    {data.priceOere > 0 ? (
                      <ActionButton
                        className={`btn btn--small${t.paidAt ? ' btn--ghost' : ' btn--secondary'}`}
                        pending={action.pending}
                        onClick={() => saetKryds(t.id, !t.paidAt)}
                      >
                        {t.paidAt ? 'Fortryd betalt' : 'Markér betalt'}
                      </ActionButton>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}

            {data.priceOere > 0 ? (
              <p className="tiny muted mt-1">
                Krydset sættes af dig, ikke af MobilePay. Appen får ikke besked, når nogen
                betaler.
              </p>
            ) : null}

            <p className="mt-2">
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => setAaben(false)}
              >
                Skjul
              </button>
            </p>
          </>
        )}
      </AsyncSection>
    </div>
  );
}

/**
 * HJÆLPERNE TIL ET ARRANGEMENT.
 *
 * Frivilligopgaverne har eksisteret hele tiden, men de blev oprettet et
 * andet sted end arrangementet, og derfor blev de sjældent oprettet. Her
 * står de, hvor man i forvejen planlægger: opret opvisningen, og skriv med
 * det samme, at der skal bruges fire til oprydning.
 *
 * Foldet sammen som udgangspunkt. Listen over arrangementer skal kunne
 * skimmes, og de fleste arrangementer har ingen hjælpere.
 */
function HelpersForEvent({
  event,
  onChanged,
}: {
  event: EventsData['events'][number];
  onChanged: () => void;
}): JSX.Element {
  const [aaben, setAaben] = useState(false);
  const [title, setTitle] = useState('');
  const [time, setTime] = useState('');
  const [capacity, setCapacity] = useState('4');
  const action = useAction();

  const opret = (): void => {
    void action.run(async () => {
      /*
        Opgavens tidspunkt: samme dag som arrangementet. Er der ikke skrevet
        et klokkeslæt, bruges arrangementets eget — en hjælper, der skal møde
        et andet tidspunkt end selve arrangementet, er undtagelsen, ikke
        reglen, og skal ikke koste alle andre et felt at udfylde.
      */
      const dag = event.startsAt.slice(0, 10);
      const startsAt = time
        ? new Date(`${dag}T${time}:00`).toISOString()
        : event.startsAt;
      await api.post('/api/volunteer-shifts', {
        title,
        location: event.location || 'Se arrangementet',
        startsAt,
        capacity: Number(capacity),
        eventId: event.id,
      });
      action.setSuccess('Opgaven står nu ved arrangementet — også i familiernes kalender.');
      setTitle('');
      setTime('');
      setCapacity('4');
      onChanged();
    });
  };

  return (
    <div className="mt-2">
      {event.shifts.length > 0 ? (
        <ul className="rows">
          {event.shifts.map((s) => (
            <li key={s.id}>
              <p className="tiny">
                <strong>{s.title}</strong> — {s.signedUp} af {s.capacity} pladser
                {s.participants.length > 0 ? `: ${s.participants.join(', ')}` : ''}
              </p>
            </li>
          ))}
        </ul>
      ) : null}

      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      {aaben ? (
        <div className="card mt-2">
          <Field label="Hvad skal der hjælpes med?" htmlFor={`sh-t-${event.id}`}>
            <input
              id={`sh-t-${event.id}`}
              type="text"
              maxLength={100}
              placeholder="Oprydning efter opvisningen"
              value={title}
              onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
            />
          </Field>
          <div className="row mt-2" style={{ gap: 12, flexWrap: 'wrap' }}>
            <Field
              label="Kl. (valgfrit)"
              htmlFor={`sh-time-${event.id}`}
              hint="Tomt = samme tid som arrangementet"
            >
              <input
                id={`sh-time-${event.id}`}
                type="time"
                value={time}
                onChange={(e) => setTime((e.target as HTMLInputElement).value)}
              />
            </Field>
            <Field label="Antal hjælpere" htmlFor={`sh-cap-${event.id}`}>
              <input
                id={`sh-cap-${event.id}`}
                type="number"
                min={1}
                max={100}
                value={capacity}
                onChange={(e) => setCapacity((e.target as HTMLInputElement).value)}
              />
            </Field>
          </div>
          <div className="btn-group mt-3">
            <ActionButton className="btn btn--small" pending={action.pending} onClick={opret}>
              Tilføj opgaven
            </ActionButton>
            <button type="button" className="btn btn--secondary btn--small" onClick={() => setAaben(false)}>
              Fortryd
            </button>
          </div>
        </div>
      ) : (
        <p className="tiny">
          <button type="button" className="btn btn--ghost btn--small" onClick={() => setAaben(true)}>
            <Icon name="volunteer" size={16} />
            {event.shifts.length > 0
              ? `Hjælpere: ${event.helpersNeeded} mangler af ${event.helperSlots}`
              : 'Har du brug for hjælpere?'}
          </button>
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sikkerhedskopi                                                      */
/* ------------------------------------------------------------------ */

interface BackupStatus {
  latest: { file: string; bytes: number; takenAt: string } | null;
  count: number;
  stale: boolean;
  note: string;
}

/**
 * SIKKERHEDSKOPI — OG DEN HALVDEL, DEN IKKE LØSER.
 *
 * Målt 9. september 2026: Railway svarede «This service's volume does not
 * have any backups», og deres egen backup kræver Pro-planen. Der blev altså
 * ikke taget kopi af 1685 personers tilknytning, sæsonens 917 træninger, alt
 * fremmøde og alle beskeder.
 *
 * Serveren tager nu én kopi om dagen af sig selv. Men kopien ligger PÅ SAMME
 * DISK. Den redder foreningen, hvis noget bliver slettet ved et uheld — ikke
 * hvis serveren mister disken. Det står på skærmen, fordi en tryghed uden
 * dækning er værre end ingen: den, der tror, der er styr på det, henter
 * aldrig filen ned.
 */
export function BackupCard(): JSX.Element {
  const status = useAsync<BackupStatus>(() => api.get('/api/workspace/backup'));
  const action = useAction();

  const tag = (): void => {
    void action.run(async () => {
      const r = await api.post<{ bytes: number }>('/api/workspace/backup');
      action.setSuccess(`Kopi taget — ${(r.bytes / 1024 / 1024).toFixed(1)} MB.`);
      status.reload();
    });
  };

  return (
    <section className="section" aria-labelledby="backup">
      <SectionHead title="Sikkerhedskopi" icon="warning" id="backup" />
      <div className="card stack stack--tight">
        {action.error ? <Notice kind="error">{action.error}</Notice> : null}
        {action.success ? <Notice kind="success">{action.success}</Notice> : null}

        <AsyncSection state={status} skeletonLines={2}>
          {(data) => (
            <>
              {data.latest ? (
                <p className="small">
                  Seneste kopi: <strong>{formatDateTime(data.latest.takenAt)}</strong> ·{' '}
                  {(data.latest.bytes / 1024 / 1024).toFixed(1)} MB ·{' '}
                  {antal(data.count, 'kopi gemt', 'kopier gemt')}.
                </p>
              ) : (
                <Notice kind="warning">
                  <strong>Der er endnu ikke taget en kopi.</strong>
                </Notice>
              )}

              {data.stale && data.latest ? (
                <Notice kind="warning">
                  Den seneste kopi er over to døgn gammel. Serveren tager normalt én om dagen —
                  er den holdt op, skal nogen se på det.
                </Notice>
              ) : null}

              <p className="tiny muted">{data.note}</p>

              <div className="btn-group mt-2">
                {data.latest ? (
                  /*
                    Et almindeligt link og ikke et fetch-kald: så håndterer
                    browseren selv download af en fil på flere megabyte, og
                    filen skal aldrig gennem hukommelsen i klienten.
                  */
                  <a
                    className="btn"
                    href={`/api/workspace/backup/${encodeURIComponent(data.latest.file)}`}
                    download
                  >
                    <Icon name="profile" size={17} />
                    Hent seneste kopi
                  </a>
                ) : null}
                <ActionButton
                  className="btn btn--secondary"
                  pending={action.pending}
                  onClick={tag}
                >
                  Tag en kopi nu
                </ActionButton>
              </div>

              {/*
                HVAD FILEN ER. Den, der henter den, skal vide, hvad han
                lægger på sit skrivebord — ellers ender foreningens
                medlemsdata i en mappe uden adgangskode.
              */}
              <div className="notice notice--warning mt-3">
                <p className="small">
                  <strong>Filen indeholder alt.</strong> Hver mailadresse, hver
                  familietilknytning, alt fremmøde og alle beskeder. Gem den et sted, kun du
                  har adgang til — ikke i en delt drevmappe og ikke i en mail.
                </p>
              </div>
            </>
          )}
        </AsyncSection>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Instruktører                                                        */
/* ------------------------------------------------------------------ */

interface InstruktoerPost {
  userId?: string;
  presetId?: string;
  name: string;
  email: string;
  kind: 'konto' | 'forhaandsadgang';
  otherDepartments: string[];
}

interface HoldMedInstruktoerer {
  teamId: string;
  name: string;
  department: string;
  instructors: InstruktoerPost[];
}

interface InstruktoerSvar {
  departments: string[];
  teams: HoldMedInstruktoerer[];
  udenInstruktoer: number;
}

/**
 * «Hvem træner hvad i min afdeling.»
 *
 * SIDEN FINDES, FORDI ET TAL VAR FOR STORT: 29 af 30 svømmehold og 6 af 7
 * badmintonhold stod uden instruktør. Ikke fordi ingen trænede dem, men fordi
 * kun bestyrelsen kunne skrive det ind — og udvalget, der ved besked, skulle
 * bede om det. Uden en instruktør har et hold hverken aflysninger, fremmøde
 * eller holdbeskeder, så halvdelen af foreningen var reelt ikke med i appen.
 *
 * Siden viser BEVIDST ikke en liste over foreningens konti. Man tilføjer ved
 * at skrive en mailadresse — den samme, personen er oprettet med i Conventus.
 * Kender appen den ikke endnu, bliver det til en forhåndsadgang, som træder i
 * kraft, første gang hun logger ind.
 */
export function InstructorsPage(): JSX.Element {
  const state = useAsync<InstruktoerSvar>(() => api.get('/api/instruktoerer'));
  const action = useAction();
  const [aabent, setAabent] = useState<string | null>(null);
  const [mail, setMail] = useState('');

  const tilfoej = (teamId: string): void => {
    void action.run(async () => {
      const r = await api.post<{ resultat: { kind: string; email: string; teamName: string } }>(
        '/api/instruktoerer',
        { teamId, email: mail.trim() },
      );
      action.setSuccess(
        r.resultat.kind === 'forhaandsadgang'
          ? `${r.resultat.email} er skrevet på ${r.resultat.teamName}. Adgangen træder i kraft, første gang hun logger ind.`
          : `${r.resultat.email} er nu instruktør på ${r.resultat.teamName}.`,
      );
      setMail('');
      setAabent(null);
      state.reload();
    });
  };

  const fjern = (hold: HoldMedInstruktoerer, i: InstruktoerPost): void => {
    void action.run(async () => {
      const r = await api.post<{ resultat: { teamName: string; blevMedlem: boolean } }>(
        '/api/instruktoerer/fjern',
        { teamId: hold.teamId, userId: i.userId, presetId: i.presetId },
      );
      action.setSuccess(
        r.resultat.blevMedlem
          ? `${i.email} er fjernet fra ${r.resultat.teamName} og er ikke længere instruktør.`
          : `${i.email} er fjernet fra ${r.resultat.teamName}, men træner stadig andre hold.`,
      );
      state.reload();
    });
  };

  return (
    <div className="stack">
      <PageHead
        title="Instruktører"
        eyebrow="Administration"
        eyebrowIcon="roles"
        sub="Hvem træner hvilket hold i din afdeling"
      />

      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      <AsyncSection state={state} skeletonLines={4}>
        {(data) => (
          <>
            <div className="card stack stack--tight">
              <p className="small">
                Du kan udpege instruktører på hold i{' '}
                <strong>{data.departments.map(departmentLabel).join(', ')}</strong>. Andre
                afdelingers hold vises ikke her.
              </p>
              {data.udenInstruktoer > 0 ? (
                <Notice kind="warning">
                  <strong>
                    {antal(data.udenInstruktoer, 'hold mangler', 'hold mangler')} en instruktør.
                  </strong>
                  <p className="small mt-1">
                    Uden en instruktør kan holdet ikke få aflyst en træning, registreret fremmøde
                    eller sendt en besked. De står øverst på listen.
                  </p>
                </Notice>
              ) : null}
              <p className="tiny muted">
                Brug den mailadresse, personen er oprettet med i Conventus. Kender appen den ikke
                endnu, bliver den til en adgang, der venter — og virker, første gang hun logger ind.
              </p>
            </div>

            {data.teams.map((hold) => (
              <section key={hold.teamId} className="card stack stack--tight">
                <div className="row row--between">
                  <div>
                    <h2 className="h3">{hold.name}</h2>
                    <p className="tiny muted">{departmentLabel(hold.department)}</p>
                  </div>
                  {hold.instructors.length === 0 ? (
                    <span className="tag tag--warning">Mangler instruktør</span>
                  ) : null}
                </div>

                {hold.instructors.length > 0 ? (
                  <ul className="list">
                    {hold.instructors.map((i) => (
                      <li key={i.userId ?? i.presetId} className="row row--between">
                        <span>
                          <strong>{i.name || i.email}</strong>
                          {i.name ? <span className="muted"> · {i.email}</span> : null}
                          {i.kind === 'forhaandsadgang' ? (
                            <span className="tag ml-1">Har ikke logget ind endnu</span>
                          ) : null}
                          {/*
                            Står personen også i en anden afdeling, skal det
                            SES her — ellers tror man, man fjerner hende helt,
                            og det gør man ikke.
                          */}
                          {i.otherDepartments.length > 0 ? (
                            <span className="tiny muted">
                              {' '}
                              Træner også i {i.otherDepartments.map(departmentLabel).join(', ')}.
                            </span>
                          ) : null}
                        </span>
                        <ActionButton
                          className="btn btn--ghost btn--small"
                          pending={action.pending}
                          onClick={() => fjern(hold, i)}
                        >
                          Fjern
                        </ActionButton>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {aabent === hold.teamId ? (
                  <>
                    <Field
                      label="Mailadresse"
                      htmlFor={`mail-${hold.teamId}`}
                      hint="Den samme, personen er oprettet med i Conventus."
                    >
                      <input
                        id={`mail-${hold.teamId}`}
                        type="email"
                        value={mail}
                        autoComplete="off"
                        onChange={(e) => setMail((e.target as HTMLInputElement).value)}
                      />
                    </Field>
                    <div className="btn-group">
                      <ActionButton
                        className="btn"
                        pending={action.pending}
                        disabled={!mail.includes('@')}
                        onClick={() => tilfoej(hold.teamId)}
                      >
                        Gør til instruktør
                      </ActionButton>
                      <button
                        type="button"
                        className="btn btn--secondary"
                        onClick={() => {
                          setAabent(null);
                          setMail('');
                        }}
                      >
                        Fortryd
                      </button>
                    </div>
                  </>
                ) : (
                  <p>
                    <button
                      type="button"
                      className="btn btn--secondary btn--small"
                      onClick={() => {
                        setAabent(hold.teamId);
                        setMail('');
                      }}
                    >
                      <Icon name="roles" size={16} />
                      Tilføj instruktør
                    </button>
                  </p>
                )}
              </section>
            ))}
          </>
        )}
      </AsyncSection>
    </div>
  );
}
